<template>
    <div class="card overflow-hidden sleep-debt-container">
    <div class="card-body pt-3 pb-4">
      <div class="row">
        <div v-if="period" class="col-12 d-flex justify-content-between">
          <div class="pt-2 text-lighter">
            <i class="fa fa-clock"></i>
            <span class="ms-2 font-weight-bold">{{period}}</span>
          </div>
        </div>
        <div class="col-lg-8 col-12">
          <div class="text-xs text-uppercase text-primary text-bold mb-1">
            {{ header }}
          </div>
          <span class="h5 mt-2 text-uppercase">{{selectedSection}}</span>
          <div v-if="rows && rows.length > 0" class="my-4 chart">
            <canvas ref="canvasRef" class="chart-canvas"></canvas>
            <div v-if="false" class="sleep-debt-chart">
                <canvas ref="canvasRef"></canvas>
            </div>
          </div>
         <div v-else>No data found for the selected date range.</div>
        </div>

        <div v-show="rows && rows.length > 0" class="col-lg-4 col-12">
          <div
            class="d-flex flex-column ms-3 mb-3 position-relative mt-sm-4 ms-sm-0 help"
          >
            <div class="text-bolder mb-2">What is Sleep Debt?</div>
            <div class="mb-3 content">
              Sleep debt refers to the difference between the amount of sleep you need and the 
              amount you get. Over time, if you don’t get enough sleep for continuous days, 
              sleep debt builds up. Accumulating a high level of sleep debt can harm your 
              physical and mental health.

              To perform at your best, aim to keep your sleep debt under 40 minutes. Getting 
              extra sleep or taking naps can help reduce your sleep debt. Depending on how 
              much debt you’ve accumulated, it may take several nights of sufficient sleep to fully 
              recover.
            </div>
            <div class="text-bolder mt-3 mb-2">
              How is Sleep Need calculated?
            </div>
            <div class="mb-3 content">
              Sleep need is calculated daily based on your Optimal Sleep Duration (OSD), strain 
              from the previous day, and accumulated sleep debt. It reflects how much sleep you 
              need to optimize your performance.
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>  
</template>

<script>  
import {
  Chart, BarController, BarElement,CategoryScale, LinearScale, PointElement,
  LineController,  LineElement, Tooltip, Legend,  Filler, } from "chart.js";

Chart.register(BarController, BarElement, CategoryScale, LinearScale, PointElement, LineController, LineElement, Tooltip, Legend, Filler);

export default {
  name: "SleepDebtNeedComboChart",
  props: {
    rows: {
      type: Array,
      default: () => [],
      required: true,
    },
    maxSleepDebtMinutes: { type: Number, default: 140 }, // left axis max
    id: {
      type: String,
      default: "sleep-debt-and-needed-chart",
    },
    period: {
      type: String,
      default: "",
    },
    header: {
      type: String,
      default: "Sleep Chart",
    },
    title: {
      type: String,
      default: "Today",
    },
    selectedSection: {
      type: String,
      default: "ALL",
    },
    height: {
      type: [String, Number],
      default: "200",
    },
  },

  data() {
    return {
      chart: null,
      barColor: "#8a2be2", // purple
      lineColor: "#f6b100", // yellow/orange
      gridColor: "rgba(255,255,255,0.12)",
      tickColor: "rgba(255,255,255,0.75)",
      titleColor: "rgba(255,255,255,0.75)",
    };
  },

  mounted() {
    this.renderChart();
    window.addEventListener("resize", this.handleResize, { passive: true });
  },

  beforeUnmount() {
    window.removeEventListener("resize", this.handleResize);
    this.destroyChart();
  },

  watch: {
    rows: {
      deep: true,
      handler() {
        // Re-render to keep it simple and reliable
        this.renderChart();
      },
    },
  },

  methods: {
    handleResize() {
      // Chart.js handles responsive layout; this helps in some container resize cases.
      if (this.chart) this.chart.resize();
    },

    destroyChart() {
      if (this.chart) {
        this.chart.destroy();
        this.chart = null;
      }
    },

    // Convert minutes -> "H:MM" (e.g., 94 -> "1:34")
    formatMinutesToHMM(totalMinutes) {
      const m = Math.max(0, Number(totalMinutes) || 0);
      const h = Math.floor(m / 60);
      const mm = String(m % 60).padStart(2, "0");
      return `${h}:${mm}`;
    },

    buildLabelPlugin() {
      const vm = this;

      // Draws "0:20" style labels near line points, similar to your screenshot.
      return {
        id: "linePointValueLabels",
        afterDatasetsDraw(chart) {
          const { ctx } = chart;

          // Find the line dataset index by type
          const lineIndex = chart.data.datasets.findIndex((d) => d.type === "line");
          if (lineIndex === -1) return;

          const meta = chart.getDatasetMeta(lineIndex);
          if (!meta || !meta.data) return;

          ctx.save();
          ctx.font = "12px sans-serif";
          ctx.textAlign = "center";
          ctx.textBaseline = "bottom";
          ctx.fillStyle = vm.lineColor;

          meta.data.forEach((pt, i) => {
            const v = chart.data.datasets[lineIndex].data[i];
            const label = vm.formatMinutesToHMM(v);
            const x = pt.x;
            const y = pt.y - 8; // slightly above the point
            ctx.fillText(label, x, y);
          });

          ctx.restore();
        },
      };
    },

    renderChart() {
      this.destroyChart();

      const labels = this.rows.map((r) => r.name);
      const sleepDebt = this.rows.map((r) => Number(r.sleepDebtMinutes) || 0);
      const sleepNeed = this.rows.map((r) => Number(r.sleepNeedPct) || 0);

      const ctx = this.$refs.canvasRef?.getContext("2d");
      if (!ctx) return;

      const labelPlugin = this.buildLabelPlugin();

      this.chart = new Chart(ctx, {
        type: "bar",
        data: {
          labels,
          datasets: [
            {
              type: "line",
              label: "Sleep Debt",
              data: sleepDebt,
              yAxisID: "yDebt",
              borderColor: this.lineColor,
              backgroundColor: this.lineColor,
              pointBackgroundColor: this.lineColor,
              pointBorderColor: this.lineColor,
              pointRadius: 3,
              pointHoverRadius: 4,
              borderWidth: 2,
              tension: 0.25,
            },
            {
              type: "bar",
              label: "Sleep Need (%)",
              data: sleepNeed,
              yAxisID: "yNeed",
              backgroundColor: this.barColor,
              borderRadius: 6,
              barPercentage: 0.55,
              categoryPercentage: 0.65,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          layout: { padding: { top: 8, right: 12, bottom: 0, left: 8 } },
          plugins: {
            legend: {
              position: "bottom",
              labels: {
                color: this.tickColor,
                boxWidth: 14,
                boxHeight: 14,
              },
            },
            tooltip: {
              mode: "index",
              intersect: false,
              callbacks: {
                label: (ctx) => {
                  const dsLabel = ctx.dataset.label || "";
                  const v = ctx.parsed.y;
                  if (ctx.dataset.type === "line") {
                    return `${dsLabel}: ${this.formatMinutesToHMM(v)}`;
                  }
                  return `${dsLabel}: ${v}%`;
                },
              },
            },
          },
          interaction: {
            mode: "index",
            intersect: false,
          },
          scales: {
            x: {
              stacked: true,
              ticks: {
                autoSkip: false,
                maxRotation: 60,
                minRotation: 60,
                font: { size: 10 },
                color: this.tickColor,
                padding: 6,
              },
              grid: {
                display: true,
                drawBorder: false,
                color: "rgba(255,255,255,0.08)",
              },
            },
            yDebt: {
              position: "left",
              min: 0,
              max: this.maxSleepDebtMinutes,
              ticks: {
                color: this.tickColor,
                stepSize: 20,
              },
              title: {
                display: true,
                text: "AVG SLEEP DEBT (MINUTES)",
                color: this.titleColor,
                font: { size: 12, weight: "600" },
              },
              grid: {
                color: this.gridColor,
              },
            },
            yNeed: {
              position: "right",
              min: 0,
              max: 100,
              ticks: {
                color: this.tickColor,
                stepSize: 20,
                callback: (v) => v,
              },
              title: {
                display: true,
                text: "SLEEP NEED (%)",
                color: this.titleColor,
                font: { size: 12, weight: "600" },
              },
              grid: {
                drawOnChartArea: false,
              },
            },
          },
        },
        plugins: [labelPlugin],
      });
    },
  },
};
</script>

<style scoped>
.sleep-debt-container {
    padding-top: 0 !important;
    border-top-left-radius: 0 !important;
    border-top-right-radius: 0 !important;
}
.sleep-debt-chart {
  /* Match the wide/short look from the screenshot */
  height: 260px;
  width: 100%;
  background: #1f2b2a; /* dark teal-ish */
  border-radius: 10px;
  padding: 12px;
  box-sizing: border-box;
}
</style>
